import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { getAppUserByEmail } from '../data/supabaseApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAppUser = useCallback(async (email) => {
    if (!email) {
      setAppUser(null);
      return;
    }
    const user = await getAppUserByEmail(email);
    setAppUser(user);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user?.email) {
        fetchAppUser(s.user.email);
      } else {
        setAppUser(null);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user?.email) {
        fetchAppUser(s.user.email);
      } else {
        setAppUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchAppUser]);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    let appUserRes = null;
    if (data?.user?.email) {
      appUserRes = await getAppUserByEmail(data.user.email);
      setAppUser(appUserRes);
    }
    return { data, appUser: appUserRes };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setAppUser(null);
  }, []);

  const value = {
    session,
    appUser,
    loading,
    signIn,
    signOut,
    isAdmin: appUser?.role === 'admin',
    isMember: appUser?.role === 'member',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
